import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import SingleSelect, {
  singleSelectVariant
} from "@/components/controls/SingleSelect";
import { useEntitySelect } from "@/services/hooks/entities/useEntitySelect";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { LICENSE_TYPE_ID, useProducts } from "@/entities/settings/hooks";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import {
  getEntityTypeFromDisplayType,
  isProductsDisplayType,
  isLicensesDisplayType
} from "@/utils/entityFieldHelpers";
import { AddButtonFooter } from "@/components/ui/add-button-footer";
import { map } from "lodash";
import { getPersonFixedOptions } from "@/entities/people/types";
import NoLicensesEmptyState from "@/components/controls/NoLicensesEmptyState";
import { useCompanies } from "@/pages/companies/hooks";
import { UserStatus } from "@/entities/users/types";

interface EntitySelectFieldProps {
  fieldName: string;
  displayType: string;
  value?: string | null;
  onChange?: (value: string | null) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  showSearch?: boolean;
  filters?: any;
  onCreateNew?: () => void;
  createNewLabel?: string;
  dependentValues?: Record<string, string | null>;
  excludeIds?: string[];
  config?: any; // API field configuration
  variant?: singleSelectVariant;
  showFixedOptions?: boolean; // Show fixed options (Unknown, None) at the top for people fields
  customFixedOptions?: any[];
  hasError?: boolean;
}

const EntitySelectField: React.FC<EntitySelectFieldProps> = ({
  fieldName,
  displayType,
  value,
  onChange,
  placeholder,
  required = false,
  disabled = false,
  showSearch = true,
  filters,
  onCreateNew,
  createNewLabel,
  dependentValues,
  excludeIds,
  config,
  variant = "inline",
  showFixedOptions = false,
  customFixedOptions,
  hasError = false
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const clearable = !required && Boolean(onChange);
  // The chip variant derives its remove button from Boolean(onClear) and
  // ignores `clearable`, so a required field must not receive a handler at all
  const handleClear = clearable ? () => onChange?.(null) : undefined;

  const isProductsType = isProductsDisplayType(displayType);
  const isLicensesType = isLicensesDisplayType(displayType);
  const entityType = getEntityTypeFromDisplayType(displayType);

  const productsData = useProducts();
  const products = productsData.data?.products ?? [];

  // Fetch company data to get parent company for people filters
  const companyIdForPeople =
    displayType === FieldDisplayType.PEOPLE ? dependentValues?.companyId : null;
  const {
    entities: [companyDataForPeople]
  } = useCompanies(companyIdForPeople ? [companyIdForPeople] : []);

  const dependencyFilters = React.useMemo(() => {
    const baseFilters = filters || {};

    if (displayType === FieldDisplayType.PEOPLE && dependentValues?.companyId) {
      // Include people from both the company and its parent company
      const companyIds = [dependentValues.companyId];
      if (companyDataForPeople?.parentCompany) {
        companyIds.push(companyDataForPeople.parentCompany);
      }
      return {
        ...baseFilters,
        companyId:
          companyIds.length > 1 ? { in: companyIds } : dependentValues.companyId
      };
    }

    if (displayType === FieldDisplayType.USERS) {
      return {
        ...baseFilters,
        status: [UserStatus.ACTIVE]
      };
    }

    if (
      displayType === FieldDisplayType.PARTNER_CONTACTS &&
      dependentValues?.partner
    ) {
      return {
        ...baseFilters,
        partner: dependentValues.partner
      };
    }

    return baseFilters;
  }, [
    filters,
    displayType,
    dependentValues,
    companyDataForPeople?.parentCompany
  ]);

  const { options, isLoading, onSearch, setSearchTerm } = useEntitySelect({
    entityType: entityType ?? EntityType.DEAL,
    selectedId: value,
    filters: dependencyFilters,
    excludeIds,
    alwaysEnabled: !isProductsType && !isLicensesType && !!entityType
  });

  if (isProductsType) {
    const productOptions = map(products, (product) => ({
      id: product.id,
      displayName: product.name
    }));

    const handleProductSelection = (selectedValue: string | number) => {
      onChange?.(String(selectedValue));
    };

    const effectivePlaceholder =
      placeholder || config?.placeholder || t("common.selectProduct");

    return (
      <SingleSelect
        fieldName={fieldName}
        options={productOptions}
        selectedValue={value || ""}
        onSelection={handleProductSelection}
        placeholder={effectivePlaceholder}
        disabled={disabled}
        showSearch={showSearch}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        className="h-9"
        variant={variant}
        hasError={hasError}
        clearable={clearable}
        onClear={handleClear}
      />
    );
  }

  if (isLicensesType) {
    const productId = dependentValues?.productId;

    const licenseOptions =
      products.length > 0 && productId
        ? (() => {
            const product = products.find((p) => p.id === productId);
            if (!product?.serviceItems) return [];
            return product.serviceItems
              .filter((item) => item.typeId === LICENSE_TYPE_ID)
              .map((item) => ({
                id: item.id,
                displayName: item.name
              }));
          })()
        : [];

    const handleLicenseSelection = (selectedValue: string | number) => {
      onChange?.(String(selectedValue));
    };

    const effectivePlaceholder =
      placeholder || config?.placeholder || t("common.selectLicense");

    // Check if product is selected but has no license service items
    const hasNoLicenses = productId && licenseOptions.length === 0;

    return (
      <SingleSelect
        fieldName={fieldName}
        options={licenseOptions}
        selectedValue={value || ""}
        onSelection={handleLicenseSelection}
        placeholder={effectivePlaceholder}
        disabled={disabled || !productId}
        showSearch={showSearch && !hasNoLicenses}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        className="h-9"
        variant={variant}
        popOverHeader={
          hasNoLicenses ? (
            <NoLicensesEmptyState
              labels={{
                title: t("common.noLicensesTitle"),
                description: t("common.noLicensesDescription")
              }}
            />
          ) : undefined
        }
        hasError={hasError}
        clearable={clearable}
        onClear={handleClear}
      />
    );
  }

  if (!entityType) {
    console.warn(`Unknown entity display type: ${displayType}`);
    return null;
  }

  const handleSelection = (selectedValue: string | number) => {
    setIsOpen(false);
    onChange?.(String(selectedValue));
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchTerm("");
    }
  };

  const getDefaultPlaceholder = () => {
    switch (displayType) {
      case FieldDisplayType.USERS:
        return t("common.selectUser");
      case FieldDisplayType.COMPANIES:
        return t("common.selectCompany");
      case FieldDisplayType.PEOPLE:
        return t("common.selectPerson");
      case FieldDisplayType.DEALS:
        return t("common.selectDeal");
      case FieldDisplayType.LEADS:
        return t("common.selectLead");
      case FieldDisplayType.PARTNERS:
        return t("common.selectPartner");
      case FieldDisplayType.PARTNER_CONTACTS:
        return t("common.selectPartnerContact");
      case FieldDisplayType.PRODUCTS:
        return t("common.selectProduct");
      case FieldDisplayType.LICENSES:
        return t("common.selectLicense");
      default:
        return t("select");
    }
  };

  const popOverFooter =
    onCreateNew && createNewLabel ? (
      <AddButtonFooter
        buttonText={createNewLabel}
        onClick={() => {
          setIsOpen(false);
          onCreateNew();
        }}
      />
    ) : undefined;

  const effectivePlaceholder =
    placeholder || config?.placeholder || getDefaultPlaceholder();

  const fixedOptions = useMemo(() => {
    if (showFixedOptions && displayType === FieldDisplayType.PEOPLE) {
      return getPersonFixedOptions(t);
    }
    return [];
  }, [showFixedOptions, displayType, t]);

  return (
    <SingleSelect
      fieldName={fieldName}
      options={options}
      fixedOptions={customFixedOptions ?? fixedOptions}
      selectedValue={value || ""}
      onSelection={handleSelection}
      placeholder={effectivePlaceholder}
      disabled={disabled}
      entity={entityType}
      showSearch={showSearch}
      onSearch={onSearch}
      isLoading={isLoading}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      popOverFooter={popOverFooter}
      className="h-9"
      variant={variant}
      hasError={hasError}
      showSelectedItemSubtitle={false}
      clearable={clearable}
      onClear={handleClear}
    />
  );
};

export default EntitySelectField;
